import { dramas, type Drama } from "@/mocks/dramas";

export function formatViews(views: number): string {
  if (views >= 100000000) {
    return `${(views / 100000000).toFixed(1)}亿`;
  }
  if (views >= 10000) {
    return `${(views / 10000).toFixed(0)}万`;
  }
  return String(views);
}

export function getDramaById(id: string): Drama | undefined {
  return dramas.find((item) => item.id === id);
}

export function getLatestDramas(limit?: number): Drama[] {
  const sorted = [...dramas].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function getHotDramas(limit?: number): Drama[] {
  const sorted = [...dramas].sort((a, b) => b.views - a.views);
  return typeof limit === "number" ? sorted.slice(0, limit) : sorted;
}

export function getDramasByTopic(topicId: string): Drama[] {
  return dramas.filter((item) => item.topics.includes(topicId));
}

export function getRelatedDramas(current: Drama, limit = 5): Drama[] {
  const scored = dramas
    .filter((item) => item.id !== current.id)
    .map((item) => {
      const genreScore = item.genres.filter((g) => current.genres.includes(g)).length * 3;
      const topicScore = item.topics.filter((t) => current.topics.includes(t)).length * 2;
      const categoryScore = item.category === current.category ? 1 : 0;
      return { item, score: genreScore + topicScore + categoryScore };
    })
    .sort((a, b) => b.score - a.score || b.item.rating - a.item.rating);
  return scored.slice(0, limit).map((entry) => entry.item);
}

export interface FilterState {
  categories: string[];
  genres: string[];
  year: string;
  sort: string;
}

export const defaultFilters: FilterState = {
  categories: [],
  genres: [],
  year: "all",
  sort: "latest",
};

export function filterDramas(list: Drama[], filters: FilterState, keyword = ""): Drama[] {
  const kw = keyword.trim().toLowerCase();
  const result = list.filter((item) => {
    if (kw) {
      const haystack = [
        item.title,
        item.director,
        item.cast.join(" "),
        item.genres.join(" "),
        item.tags.join(" "),
        item.summary,
        String(item.year),
      ]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(kw)) return false;
    }
    if (filters.categories.length > 0 && !filters.categories.includes(item.category)) {
      return false;
    }
    if (filters.genres.length > 0 && !item.genres.some((g) => filters.genres.includes(g))) {
      return false;
    }
    if (filters.year !== "all") {
      if (filters.year === "2010s") {
        if (item.year < 2010 || item.year > 2019) return false;
      } else if (filters.year === "2000s") {
        if (item.year < 2000 || item.year > 2009) return false;
      } else if (item.year !== Number(filters.year)) {
        return false;
      }
    }
    return true;
  });

  if (filters.sort === "hot") {
    result.sort((a, b) => b.views - a.views);
  } else if (filters.sort === "rating") {
    result.sort((a, b) => b.rating - a.rating);
  } else {
    result.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }
  return result;
}

export function countActiveFilters(filters: FilterState): number {
  return (
    filters.categories.length + filters.genres.length + (filters.year !== "all" ? 1 : 0)
  );
}

export function toggleValue(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}